import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronDown, Sparkles } from 'lucide-react';
import { candidateStatusBadge, listCandidateStatusKey } from '../../../shared/utils/candidateStatus';

/**
 * The job's applicants as a ranked board rather than a flat table.
 *
 * A table gives every row equal weight and makes you read the score column to
 * work out who to look at. This orders by score, states the band, and shows
 * how much of the requirement each person covers — so the shape of the pool is
 * readable before you open anyone.
 */

const MUTED = 'var(--text-muted)';

// Bands are read off the resume score, so the labels name the score range and
// nothing else — "or better" implied a comparison against something the
// numbers were not measuring.
const BANDS = [
  { key: 'strong', label: 'Strong (75–100)', color: 'var(--green)', test: (s) => s >= 75 },
  { key: 'good', label: 'Good (55–74)', color: 'var(--accent)', test: (s) => s >= 55 && s < 75 },
  { key: 'partial', label: 'Partial (35–54)', color: 'var(--orange)', test: (s) => s >= 35 && s < 55 },
  { key: 'weak', label: 'Weak (0–34)', color: 'var(--red)', test: (s) => s < 35 },
];

function bandOf(score) {
  return BANDS.find((b) => b.test(score)) || BANDS[BANDS.length - 1];
}

function verdict(score) {
  if (score >= 85) return 'Excellent match';
  if (score >= 75) return 'Strong match';
  if (score >= 55) return 'Good match';
  if (score >= 35) return 'Partial match';
  return 'Weak match';
}

function initials(name) {
  return String(name || '?')
    .split(/\s+/).filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

function Kpi({ label, value, unit, sub, color }) {
  return (
    <div style={{
      flex: '1 1 150px', minWidth: 148,
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '13px 15px',
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em',
        textTransform: 'uppercase', color: MUTED,
      }}>{label}</div>
      <div style={{
        marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 5,
        fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-mono)',
        color: color || 'var(--text-primary)',
      }}>
        {value}
        {unit && <span style={{ fontSize: 12, fontWeight: 500, color: MUTED, fontFamily: 'var(--font)' }}>{unit}</span>}
      </div>
      {sub && <div style={{ marginTop: 2, fontSize: 11.5, color: MUTED }}>{sub}</div>}
    </div>
  );
}

/** Horizontal stacked bar showing how the pool splits across bands. */
function Distribution({ scored, candidatesTotal }) {
  const counts = BANDS.map((b) => ({ ...b, count: scored.filter((c) => b.test(c.resume_score)).length }));
  const total = scored.length || 1;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
        <div className="section-title" style={{ marginBottom: 0 }}>Resume score distribution</div>
        <span style={{ fontSize: 11.5, color: MUTED }}>
          {scored.length} of {candidatesTotal} scored
        </span>
      </div>
      <div style={{
        display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden',
        background: 'var(--bg-secondary)', gap: 2, marginTop: 12,
      }}>
        {counts.filter((b) => b.count > 0).map((b) => (
          <div key={b.key} title={`${b.label}: ${b.count}`}
            style={{ width: `${(b.count / total) * 100}%`, background: b.color }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 }}>
        {counts.map((b) => (
          <span key={b.key} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12.5, color: b.count ? 'var(--text-secondary)' : 'var(--text-faint)',
          }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, opacity: b.count ? 1 : 0.35 }} />
            <b style={{ fontFamily: 'var(--font-mono)', color: b.count ? 'var(--text-primary)' : 'var(--text-faint)' }}>{b.count}</b>
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Row({ c, rank, threshold, open, onToggle }) {
  const score = c.resume_score;
  const isScored = score !== null && score !== undefined;
  const band = isScored ? bandOf(score) : { color: MUTED };
  const cov = c.requirement_coverage || {};
  const must = cov.must_have;
  const nice = cov.nice_to_have;
  const skills = (c.skills || []).slice(0, 3);
  const status = candidateStatusBadge(listCandidateStatusKey(c));

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', marginBottom: 8, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        className="r-rank-row"
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: '13px 15px', display: 'grid', gap: 13,
          gridTemplateColumns: '26px 38px minmax(0,1.7fr) 78px 96px 18px',
          alignItems: 'center', font: 'inherit',
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
      >
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: MUTED }}>
          {String(rank).padStart(2, '0')}
        </span>
        <span style={{
          width: 36, height: 36, borderRadius: '50%', background: 'var(--gradient)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 12.5, flexShrink: 0,
        }}>{initials(c.name)}</span>

        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)' }}>{c.name || '—'}</span>
            <span className={`badge ${status.cls}`} style={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>{status.label}</span>
          </span>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 1 }}>
            {[c.current_role, c.current_company].filter(Boolean).join(' · ') || c.email}
          </div>
          {skills.length > 0 && (
            <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
              {skills.map((s) => <span key={s} className="tag" style={{ fontSize: 11 }}>{s}</span>)}
              {(c.skills || []).length > 3 && (
                <span style={{ fontSize: 11, color: MUTED, alignSelf: 'center' }}>
                  +{c.skills.length - 3}
                </span>
              )}
            </div>
          )}
        </span>

        <span style={{ fontSize: 12.5, color: MUTED, textAlign: 'right' }}>
          {c.total_experience_years != null
            ? <><b style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, color: 'var(--text-secondary)' }}>{c.total_experience_years}</b> yrs</>
            : '—'}
        </span>

        <span style={{ textAlign: 'right' }}>
          {isScored ? (
            <>
              <span style={{ fontSize: 21, fontWeight: 700, fontFamily: 'var(--font-mono)', color: band.color }}>
                {score}
              </span>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', color: band.color, marginTop: 1 }}>
                {verdict(score)}
              </div>
              {threshold != null && (
                <div style={{ fontSize: 10, color: score >= threshold ? 'var(--green)' : MUTED, marginTop: 1 }}>
                  {score >= threshold ? `Passes ${threshold}` : `Below ${threshold}`}
                </div>
              )}
            </>
          ) : <span style={{ fontSize: 12, color: MUTED }}>Not scored</span>}
        </span>

        <ChevronDown
          size={15} color={MUTED}
          style={{ justifySelf: 'end', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>

      {open && (
        <div style={{ padding: '12px 15px 16px' }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 11 }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Status{' '}
              <span className={`badge ${status.cls}`} style={{ whiteSpace: 'nowrap' }}>{status.label}</span>
            </span>
            {must && must.total > 0 && (
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Must-haves{' '}
                <b style={{
                  fontFamily: 'var(--font-mono)',
                  color: must.met === must.total ? 'var(--green)' : must.pct >= 70 ? 'var(--orange)' : 'var(--red)',
                }}>{must.met}/{must.total}</b>
              </span>
            )}
            {nice && nice.total > 0 && (
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Nice-to-haves <b style={{ fontFamily: 'var(--font-mono)' }}>{nice.met}/{nice.total}</b>
              </span>
            )}
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Interview{' '}
              <span className={`badge ${c.interview_status === 'Completed' ? 'badge-green'
                : c.interview_status === 'Scheduled' ? 'badge-blue'
                  : c.interview_status === 'In Progress' ? 'badge-yellow' : 'badge-gray'}`}
                style={{ whiteSpace: 'nowrap' }}>
                {c.interview_status === 'Completed' ? 'Completed'
                  : c.interview_status === 'Scheduled' ? 'Scheduled'
                    : c.interview_status === 'In Progress' ? 'In Progress'
                      : 'Not initiated'}
              </span>
            </span>
            {c.final_score != null && (
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Final <b style={{ fontFamily: 'var(--font-mono)', color: bandOf(c.final_score).color }}>{c.final_score}</b>
              </span>
            )}
          </div>

          <div className="grid-2" style={{ gap: 16 }}>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, marginBottom: 5 }}>WHY IT RANKS HERE</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {c.resume_score_summary || 'No summary recorded for this score.'}
              </div>
              {(c.resume_score_strengths || []).slice(0, 3).map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 7, lineHeight: 1.55 }}>
                  <Sparkles size={12} strokeWidth={2} color="var(--green)" style={{ flexShrink: 0, marginTop: 3 }} />{s}
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, marginBottom: 5 }}>MAIN GAPS</div>
              {(c.resume_score_gaps || []).length === 0 && (!must || must.missing.length === 0) && (
                <div style={{ fontSize: 13, color: MUTED }}>None recorded.</div>
              )}
              {must && must.missing.length > 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 7 }}>
                  Missing required: <b style={{ color: 'var(--red)' }}>{must.missing.join(', ')}</b>
                </div>
              )}
              {(c.resume_score_gaps || []).slice(0, 3).map((g, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.55 }}>
                  <AlertTriangle size={12} strokeWidth={2} color="var(--orange)" style={{ flexShrink: 0, marginTop: 3 }} />{g}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <Link to={`/candidates/${c.id}`} className="btn btn-secondary btn-sm">Open full profile</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RankedCandidates({ candidates, threshold }) {
  const [sort, setSort] = useState('score');
  const [openId, setOpenId] = useState(null);

  const scored = useMemo(
    () => candidates.filter((c) => c.resume_score !== null && c.resume_score !== undefined),
    [candidates],
  );

  const stats = useMemo(() => {
    if (scored.length === 0) return null;
    const scores = scored.map((c) => c.resume_score);
    return {
      avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      top: Math.max(...scores),
    };
  }, [scored]);

  const ordered = useMemo(() => {
    const list = [...candidates];
    if (sort === 'name') return list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    // Unscored sink to the bottom rather than sorting as zero.
    return list.sort((a, b) => {
      const as = a.resume_score ?? -1;
      const bs = b.resume_score ?? -1;
      return bs - as;
    });
  }, [candidates, sort]);

  if (candidates.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">No candidates yet</div>
          <div className="empty-state-text">Upload resumes above to start screening.</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {stats && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <Kpi label="Candidates" value={candidates.length}
              sub={scored.length === candidates.length ? 'all scored' : `${candidates.length - scored.length} unscored`} />
            <Kpi label="Average match" value={stats.avg} unit="/ 100" color={bandOf(stats.avg).color} />
            <Kpi label="Top match" value={stats.top} unit="/ 100" color={bandOf(stats.top).color} />
            {threshold != null && (
              <Kpi label={`Passing ${threshold}`} value={scored.filter((c) => c.resume_score >= threshold).length}
                unit={`/ ${scored.length}`} color="var(--green)" />
            )}
          </div>
          <Distribution scored={scored} candidatesTotal={candidates.length} />
        </>
      )}

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', marginBottom: 11,
      }}>
        <div className="section-title" style={{ marginBottom: 0 }}>Ranked candidates</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em', color: MUTED }}>Sort</span>
          {[['score', 'Match score'], ['name', 'Name A–Z']].map(([key, label]) => (
            <button
              key={key} type="button" onClick={() => setSort(key)}
              style={{
                fontSize: 12, fontWeight: sort === key ? 600 : 500, padding: '5px 11px',
                borderRadius: 999, cursor: 'pointer',
                border: sort === key ? '1px solid transparent' : '1px solid var(--border-light)',
                background: sort === key ? 'var(--gradient)' : 'var(--bg-card)',
                color: sort === key ? '#fff' : 'var(--text-secondary)',
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      {ordered.map((c, i) => (
        <Row
          key={c.id} c={c} rank={i + 1} threshold={threshold}
          open={openId === c.id}
          onToggle={() => setOpenId(openId === c.id ? null : c.id)}
        />
      ))}
    </div>
  );
}
